import React, { useEffect, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { IoCheckmark, IoChevronDown } from "react-icons/io5";
import { MODELS, TRAITS, isModel } from "../../../constants/devices";
import { sendControl } from "../../../services/deviceControl";
import { useToggleTop } from "../../../components/common/ToggleTop";
import Sheet from "../../../components/layout/app/Sheet";
import {
  DEFAULT_ERROR,
  SettingsPage,
  SettingsSkeleton,
  UPDATED,
  Unavailable,
  clamp,
  readConfigDevice,
  settingsPayload,
  useAlive,
} from "./shared";
import "../../../assets/styles/app-settings.css";

// Suction speeds in the order the device numbers them (0, 1, 2).
const SPEEDS = ["Low", "Medium", "High"];

const numberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

// Suction speed and volume from the first settings entry. As in the app,
// Medium stands in when no speed is stored.
const readVacuum = (source) => {
  const settings = source?.deviceSettings?.[0]?.settings;
  const suction = numberOrNull(settings?.suctionPower);
  return {
    speed: suction === null ? 1 : suction === 0 ? 0 : suction === 1 ? 1 : 2,
    volume: numberOrNull(settings?.volume),
  };
};

// Robot vacuum settings for WGRVC_2 (app/screens/DeviceSettings/
// VacuumSettings.js): Suction Speed (trait 1106) and Volume Control (trait
// 1109), each picked in a bottom sheet and sent on Save.
function VacuumSettings() {
  const { state } = useLocation();
  const toast = useToggleTop();
  const alive = useAlive();
  const device = state?.data;
  const supported = isModel(device, MODELS.WGRVC_2);
  // undefined while reading.
  const [values, setValues] = useState();
  // Which sheet is open ("speed" or "volume") and the value being picked in it.
  const [sheet, setSheet] = useState(null);
  const [draft, setDraft] = useState(0);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!device || !supported) return undefined;
    let current = true;
    readConfigDevice(device.applianceId).then((fresh) => {
      if (current) setValues(readVacuum(fresh ?? device));
    });
    return () => {
      current = false;
    };
  }, [device, supported]);

  if (!device) return <Redirect to="/dashboard" />;

  // The volume slider runs 0-99; an unknown volume starts at the top, where
  // the app's default of 100 would sit.
  const openSheet = (which) => {
    setDraft(which === "speed" ? values.speed : clamp(values.volume ?? 99, 0, 99));
    setSheet(which);
  };

  const save = async () => {
    if (busy || !sheet) return;
    const which = sheet;
    const value = draft;
    setSheet(null);
    setBusy("Updating settings…");
    try {
      await sendControl(
        settingsPayload(device, {
          id: 1,
          trait: Number(which === "speed" ? TRAITS.VACUUM_SUCTION : TRAITS.VACUUM_VOLUME),
          value,
        })
      );
      if (alive.current) setValues((current) => ({ ...current, [which]: value }));
      toast(UPDATED, "green");
    } catch (error) {
      toast(DEFAULT_ERROR, "red");
    } finally {
      if (alive.current) setBusy(null);
    }
  };

  const saveButton = (
    <button type="button" className="hx-set-sheet-save" onClick={save}>
      Save
    </button>
  );

  let body;
  if (!supported) {
    body = <Unavailable />;
  } else if (values === undefined) {
    body = <SettingsSkeleton />;
  } else {
    body = (
      <>
        <section className="hx-set-card">
          <button type="button" className="hx-set-row" aria-haspopup="dialog" onClick={() => openSheet("speed")}>
            <span className="hx-set-row-text">Suction Speed</span>
            <span className="hx-set-row-value">{SPEEDS[values.speed]}</span>
            <IoChevronDown aria-hidden="true" />
          </button>
        </section>
        <section className="hx-set-card">
          <button type="button" className="hx-set-row" aria-haspopup="dialog" onClick={() => openSheet("volume")}>
            <span className="hx-set-row-text">Volume Control</span>
            <span className="hx-set-row-value">{values.volume ?? "–"}</span>
            <IoChevronDown aria-hidden="true" />
          </button>
        </section>
      </>
    );
  }

  return (
    <SettingsPage title="Vacuum Settings" device={device} busy={busy}>
      {body}

      <Sheet open={sheet === "speed"} onClose={() => setSheet(null)} title="Suction Mode" actions={saveButton}>
        <div className="hx-set-choices" role="radiogroup" aria-label="Suction Speed">
          {SPEEDS.map((label, index) => (
            <label key={label} className={`hx-set-choice ${draft === index ? "is-checked" : ""}`}>
              <input
                type="radio"
                name="hx-set-speed"
                value={index}
                checked={draft === index}
                onChange={() => setDraft(index)}
              />
              <span>{label}</span>
              {draft === index ? <IoCheckmark aria-hidden="true" /> : null}
            </label>
          ))}
        </div>
      </Sheet>

      <Sheet open={sheet === "volume"} onClose={() => setSheet(null)} title="Volume Control" actions={saveButton}>
        <div className="hx-set-volume">
          <p className="hx-set-volume-value" aria-hidden="true">
            {draft} %
          </p>
          <input
            className="hx-set-range"
            type="range"
            min={0}
            max={99}
            step={1}
            value={draft}
            aria-label="Volume"
            aria-valuetext={`${draft} %`}
            onChange={(event) => setDraft(Number(event.target.value))}
          />
        </div>
      </Sheet>
    </SettingsPage>
  );
}

export default VacuumSettings;
